import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { api, errDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from "@/components/ui/command";
import { ChevronsUpDown, Check, Loader2 } from "lucide-react";

// One fetch of the assignable-owner list per page load, shared by every
// row's picker — the list is "active employees who can work Cases", as
// decided by the SERVER (GET /cases/assignable-owners), never re-derived here.
let ownersPromise = null;
export const loadAssignableOwners = () => {
  if (!ownersPromise) {
    ownersPromise = api.get("/cases/assignable-owners").then((r) => r.data)
      .catch((e) => { ownersPromise = null; throw e; });
  }
  return ownersPromise;
};

/**
 * Searchable Process-owner picker (Workstream B / B1). Assigns or
 * reassigns the Case's PROCESS owner only — the Sales owner is a different
 * field on a different record and is never touched here.
 *
 * Mirrors the detail page's Reassign rule exactly: a reason is required
 * when the Case already has a Process owner; claiming an Unassigned Case
 * needs none. The server is the authority (capability gate, assignable
 * list, compare-and-swap, idempotent retry — a duplicate submit of the
 * same assignment is a no-op with no second audit event or notification).
 */
export function ProcessOwnerPicker({ caseId, ownerId, ownerName, onAssigned, disabled, testId }) {
  const [open, setOpen] = useState(false);
  const [owners, setOwners] = useState(null);
  const [picked, setPicked] = useState(null);
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const inFlight = useRef(false);

  useEffect(() => {
    if (!open || owners) return;
    loadAssignableOwners().then(setOwners).catch((e) => {
      setOwners([]); toast.error(errDetail(e, "Could not load employees"));
    });
  }, [open, owners]);

  useEffect(() => { if (!open) { setPicked(null); setReason(""); } }, [open]);

  const needsReason = !!ownerId;

  const submit = async (target) => {
    if (inFlight.current) return; // double-click / double-Enter guard
    const who = target || picked;
    if (!who) return;
    if (who.user_id === ownerId) { setOpen(false); return; }
    if (needsReason && !reason.trim()) return;
    inFlight.current = true; setSaving(true);
    // Optimistic: the row shows the new owner immediately; the parent's
    // onAssigned refetch then confirms (or the catch below reverts).
    onAssigned?.({ optimistic: true, process_owner_user_id: who.user_id, process_owner_name: who.name });
    try {
      const { data } = await api.patch(`/cases/${caseId}/process-owner`, {
        process_owner_user_id: who.user_id,
        expected_current_owner_user_id: ownerId || null,
        reason: needsReason ? reason.trim() : null,
      });
      toast.success(data.status === "unchanged" ? "Already assigned" : `Process owner → ${who.name}`);
      setOpen(false);
      onAssigned?.({ optimistic: false, ...data });
    } catch (e) {
      toast.error(errDetail(e, "Could not assign"));
      onAssigned?.({ optimistic: false, revert: true });
    } finally {
      inFlight.current = false; setSaving(false);
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="sm" disabled={disabled || saving}
          className="h-7 px-2 -ml-2 text-sm font-normal justify-between gap-1 max-w-[12rem]"
          data-testid={testId || `owner-picker-${caseId}`}
          aria-label={`Process owner: ${ownerName || "Unassigned"} — change`}
          onClick={(e) => e.stopPropagation()}>
          <span className={`truncate ${ownerName ? "" : "text-muted-foreground"}`}>
            {ownerName || "Unassigned"}
          </span>
          {saving ? <Loader2 className="h-3 w-3 animate-spin shrink-0" />
            : <ChevronsUpDown className="h-3 w-3 opacity-50 shrink-0" />}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-72 p-0" align="start" onClick={(e) => e.stopPropagation()}>
        {!picked || !needsReason ? (
          <Command>
            <CommandInput placeholder="Search employees…" data-testid={`owner-picker-search-${caseId}`} />
            <CommandList>
              <CommandEmpty>{owners === null ? "Loading…" : "No assignable employee found."}</CommandEmpty>
              <CommandGroup heading="Assign Process owner">
                {(owners || []).map((u) => (
                  <CommandItem key={u.user_id} value={`${u.name} ${u.role} ${u.email || ""}`}
                    data-testid={`owner-option-${caseId}-${u.user_id}`}
                    onSelect={() => { if (needsReason && u.user_id !== ownerId) setPicked(u); else submit(u); }}>
                    <Check className={`h-3.5 w-3.5 mr-2 ${u.user_id === ownerId ? "opacity-100" : "opacity-0"}`} />
                    <span className="truncate">{u.name}</span>
                    <span className="ml-auto text-[10px] text-muted-foreground capitalize">{u.role}</span>
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        ) : (
          <div className="p-3 space-y-2">
            <div className="text-xs">
              Reassign from <b>{ownerName}</b> to <b>{picked.name}</b>
            </div>
            <label className="text-[11px] text-muted-foreground" htmlFor={`owner-reason-${caseId}`}>
              Reason (required — this Case already has a Process owner)
            </label>
            <Textarea id={`owner-reason-${caseId}`} rows={2} autoFocus value={reason}
              data-testid={`owner-picker-reason-${caseId}`}
              onChange={(e) => setReason(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); submit(); } }} />
            <div className="flex justify-end gap-2">
              <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setPicked(null)}>Back</Button>
              <Button size="sm" className="h-7 text-xs" disabled={!reason.trim() || saving}
                data-testid={`owner-picker-confirm-${caseId}`} onClick={() => submit()}>
                {saving ? "Saving…" : "Reassign"}
              </Button>
            </div>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
